import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { humanizeApiError } from '../../../services/client'
import {
  adminGetAnnouncements,
  adminCreateAnnouncement,
  adminUpdateAnnouncement,
  adminDeleteAnnouncement,
  adminPinAnnouncement,
  adminUnpinAnnouncement,
} from '../../../services/admin'

function AnnouncementModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial
  const [title, setTitle] = useState(initial?.title ?? '')
  const [body, setBody] = useState(initial?.body ?? initial?.content ?? '')
  const [scheduledAt, setScheduledAt] = useState(initial?.scheduledAt ? new Date(initial.scheduledAt).toISOString().slice(0, 16) : '')
  const [status, setStatus] = useState(initial?.status ?? 'draft')
  const [isPinned, setIsPinned] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!title.trim()) { setError('Title is required'); return }
    if (!body.trim()) { setError('Body is required'); return }
    setLoading(true)
    setError(null)
    try {
      const payload = { title, body }
      if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString()
      if (isEdit) {
        payload.status = status
        await adminUpdateAnnouncement(initial.id, payload)
      } else {
        // isPinned is only accepted on create; edits use the pin/unpin routes.
        if (isPinned) payload.isPinned = true
        await adminCreateAnnouncement(payload)
      }
      onSaved()
    } catch (err) {
      setError(err.message ?? `Failed to ${isEdit ? 'update' : 'create'} announcement`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className="bg-surface border-4 border-on-background w-full max-w-lg flex flex-col gap-5 p-6"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">
            {isEdit ? 'Edit Announcement' : 'New Announcement'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {error && <AdminAlert type="error" message={error} />}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
              Title <span className="text-error">*</span>
            </label>
            <input
              type="text"
              placeholder="Announcement title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[14px] focus:outline-none focus:border-primary-container"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
              Body <span className="text-error">*</span>
            </label>
            <textarea
              rows={5}
              placeholder="Announcement content…"
              value={body}
              onChange={e => setBody(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container resize-y"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
              Schedule For (optional)
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
            />
            <p className="text-[11px] text-on-surface-variant">If set, this announcement will be scheduled instead of saved as a draft.</p>
          </div>
          {!isEdit && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} className="w-4 h-4 accent-primary-container" />
              <span className="font-bold text-[11px] uppercase tracking-widest">Pin to top of the board (max 3)</span>
            </label>
          )}
          {isEdit && (
            <div className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
              >
                {['draft', 'scheduled', 'published', 'unpublished'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : (scheduledAt ? 'Schedule' : 'Save Draft')}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Announcements() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('announcements')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null)       // null | 'new' | announcementObj
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [successMsg, setSuccessMsg] = useState(null)
  const [pinningId, setPinningId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await adminGetAnnouncements())
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSaved = () => {
    setSuccessMsg(modal === 'new' ? 'Announcement created' : 'Announcement updated')
    setTimeout(() => setSuccessMsg(null), 4000)
    setModal(null)
    load()
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await adminDeleteAnnouncement(deleteTarget.id)
      setDeleteTarget(null)
      setSuccessMsg('Announcement deleted')
      setTimeout(() => setSuccessMsg(null), 4000)
      load()
    } catch (err) {
      setError(err)
      setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  const togglePin = async (item) => {
    setPinningId(item.id)
    setError(null)
    try {
      if (item.isPinned) await adminUnpinAnnouncement(item.id)
      else await adminPinAnnouncement(item.id)
      setSuccessMsg(item.isPinned ? 'Announcement unpinned' : 'Announcement pinned')
      setTimeout(() => setSuccessMsg(null), 4000)
      await load()
    } catch (err) {
      // e.g. 400 "You can pin at most 3 announcements. Unpin one first." / 403 no permission
      setError(err)
    } finally {
      setPinningId(null)
    }
  }

  const fmtDate = d => d ? new Date(d).toLocaleString() : '—'

  return (
    <>
      {modal && (
        <AnnouncementModal
          initial={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4"
          onClick={e => { if (e.target === e.currentTarget) setDeleteTarget(null) }}
        >
          <div
            className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}
          >
            <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Delete Announcement?</h2>
            <p className="text-[13px] text-on-surface-variant">
              This will permanently delete{' '}
              <span className="font-bold text-on-surface">
                "{deleteTarget.title ?? 'this announcement'}"
              </span>. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {successMsg && <AdminAlert type="success" message={successMsg} />}
        {error && <AdminAlert type="error" message={humanizeApiError(error) || 'Failed to load announcements'} />}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[12px] text-on-surface-variant">
            Create and manage platform-wide announcements visible to all users.
          </p>
          {canEdit && <button
            onClick={() => setModal('new')}
            className="px-5 py-2.5 border-4 border-on-background bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity shrink-0"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}
          >
            + New Announcement
          </button>}
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <span className="material-symbols-outlined text-[32px] text-on-surface-variant/50 mb-2 block">campaign</span>
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">
              No announcements yet — publish one to get started
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map(item => (
              <div
                key={item.id}
                className="border-4 border-on-background bg-surface p-4 flex items-start justify-between gap-4 flex-wrap"
                style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}
              >
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-[15px] leading-tight">
                      {item.title ?? '(Untitled)'}
                    </h3>
                    {item.isPinned && (
                      <span className="inline-flex items-center gap-1 font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-2 border-primary-container bg-primary-container/15 text-on-primary-container">
                        <span className="material-symbols-outlined text-[12px]">push_pin</span>Pinned
                      </span>
                    )}
                    {item.status && (
                      <span className="font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-2 border-on-background/20 bg-surface-container text-on-surface-variant">
                        {item.status}
                      </span>
                    )}
                  </div>
                  {(item.body ?? item.content) && (
                    <p className="text-[12px] text-on-surface-variant line-clamp-2">
                      {item.body ?? item.content}
                    </p>
                  )}
                  <p className="text-[11px] text-on-surface-variant font-mono mt-1">
                    {item.updatedAt
                      ? `Updated ${fmtDate(item.updatedAt)}`
                      : `Created ${fmtDate(item.createdAt)}`}
                  </p>
                </div>
                {canEdit && <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => togglePin(item)}
                    disabled={pinningId === item.id}
                    className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded disabled:opacity-50"
                  >
                    {pinningId === item.id ? '…' : item.isPinned ? 'Unpin' : 'Pin'}
                  </button>
                  <button
                    onClick={() => setModal(item)}
                    className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteTarget(item)}
                    className="px-3 py-1.5 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors rounded"
                  >
                    Delete
                  </button>
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}